import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';
import Modal from '@amzn/awsui-components-console/modal';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Spinner from '@amzn/awsui-components-console/spinner';
import {
  ChangeControlSeverity,
  useDescribeStringGroupLazyQuery,
  useListStringsLazyQuery,
  useSubmitStringGroupTranslationMutation,
} from 'src/generated/graphql';
import {
  computeRollbackDelta,
  DuplicateStringIdError,
  isDeltaEmpty,
  RollbackDelta,
} from '../utils/rollbackDelta';
import { StringInfo } from '../types';
import {
  computeCxIdDiff,
  computeVersionDiff,
  CxIdDiff,
  DiffResult,
} from '../utils/versionDiff';
import { fetchVersionSnapshot } from '../utils/fetchVersionSnapshot';
import { fetchVersionCxIds } from '../utils/fetchVersionCxIds';
import { describeLoadFailure } from '../utils/snapshotError';
import VersionDiffTable from './VersionDiffTable';
import CxIdDiffTable from './CxIdDiffTable';
import { submitGroupForTranslation } from 'src/features/string-group-detail/utils/submitTranslationUtils';
import type { SubmitFailure } from 'src/features/string-group-detail/utils/submitTranslationUtils';
import AccessDeniedAlert from 'src/common/authz/AccessDeniedAlert';
import {
  getAccessDeniedMessage,
  getAccessDeniedNamespace,
  isAccessDenied,
} from 'src/common/authz/isAccessDenied';
import { formatDayMonthYearTimeUtc } from 'src/common/utils/dateFormatters';
import { ActiveChangeControlWindow } from 'src/common/ui/ChangeControlBanner/useChangeControlStatus';
import { rollbackEditsLiveStrings } from '../utils/rollbackEditsLiveStrings';
import ChangeControlBlockedModal from './ChangeControlBlockedModal';
import {
  MCM_ID_DESCRIPTION_KEY,
  MCM_ID_PLACEHOLDER,
  WINDOW_LABEL_KEY,
  mcmOverrideErrorKey,
} from './changeControlOverride';
import {
  NOTIFICATION_EMAIL_PLACEHOLDER,
  notificationEmailErrorKey,
} from './notificationEmail';
import { TranslateFn, useTranslation } from 'src/i18n/shim/useTranslation';
import { joinList } from 'src/common/utils/joinList';

interface RollbackModalProps {
  visible: boolean;
  version: number;
  currentVersion: number;
  namespaceId: string;
  stringGroupId: string;
  /**
   * The group's source locale. Frozen for the life of the group, so a rollback
   * re-submits the same value a routine submit does — it is not a property of the
   * version being rolled back to.
   */
  sourceLocale: string;
  /** The group's CX family. Frozen group-wide, like {@link sourceLocale}. */
  cxFamilyId: string;
  isCurrentVersionCancelled: boolean;
  /**
   * The active Restricted/Blocked (peak-freeze) window, or `null`/absent on a
   * normal day — the same value `SubmitTranslationModal` receives, from the
   * page's `useChangeControlStatus`.
   *
   * A rollback is submitted through the same mutation as a routine submit and is
   * refused by the same gate, so it needs the same MCM. Whether one is needed is
   * decided at preview time by `rollbackEditsLiveStrings`, which compares the
   * delta's edits against the current snapshot — the delta alone cannot answer,
   * because it files a string under `edited` for any differing field, variants
   * included, and pure variant additions are exempt.
   *
   * `useChangeControlStatus` FAILS OPEN, so on an outage this is `null` and the
   * modal behaves exactly as on a normal day, leaving the backend as the
   * authoritative gate (a refusal then surfaces via
   * {@link ChangeControlBlockedModal}).
   */
  changeControlWindow?: ActiveChangeControlWindow | null;
  onDismiss: () => void;
  onSuccess: (newVersion: number) => void;
}

/**
 * Recovery guidance for a change-control refusal of a rollback. The Submit-flow
 * default talks about a preserved draft and reopening Submit; neither applies
 * here — dismissing returns to the still-loaded rollback preview, where another
 * MCM can be cited on the same Confirm.
 */
const ROLLBACK_REFUSAL_RECOVERY_KEY = 'sgd-rollback-refusal-recovery';

/**
 * Frames a change by its rollback effect rather than the literal diff verb:
 * a string present only in the target version reappears, one present only in
 * the current version is lost, and a differing one changes back.
 */
function rollbackBadgeLabel(
  t: TranslateFn,
  changeType: DiffResult['changeType'],
): string {
  switch (changeType) {
    case 'added':
      return t('sgd-rollback-badge-reappear');
    case 'removed':
      return t('sgd-rollback-badge-lost');
    case 'changed':
      return t('sgd-rollback-badge-change');
  }
}

/** Called at render rather than in the fetch effect, so the sentence follows a locale change
 * without re-running the preview. */
function describePreviewFailure(
  t: TranslateFn,
  locale: string,
  err: unknown,
): SubmitFailure {
  if (err instanceof DuplicateStringIdError) {
    // Built from the error's fields, not its message, which is an English diagnostic.
    return {
      message: t('sgd-rollback-duplicate-string-id', {
        version: err.version,
        ids: joinList(locale, err.duplicateIds),
      }),
      accessDenied: false,
    };
  }
  // Its own message, not the ApolloError aggregate, which joins every error in the response.
  const accessDenied = isAccessDenied(err);
  const failure = accessDenied ? null : describeLoadFailure(t, err);
  const message = accessDenied
    ? getAccessDeniedMessage(err)
    : failure && failure.kind !== 'none'
      ? failure.text
      : '';
  return {
    message: message || t('sgd-rollback-preview-load-failed'),
    accessDenied,
    // From the same matched error, so the link and the sentence describe one refusal.
    deniedNamespace: accessDenied ? getAccessDeniedNamespace(err) : undefined,
  };
}

const RollbackModal: React.FC<RollbackModalProps> = ({
  visible,
  version,
  currentVersion,
  namespaceId,
  stringGroupId,
  sourceLocale,
  cxFamilyId,
  isCurrentVersionCancelled,
  changeControlWindow = null,
  onDismiss,
  onSuccess,
}) => {
  const { t, locale } = useTranslation();
  // Preview phase (runs on open): fetch both snapshots and compute the impact.
  const [previewLoading, setPreviewLoading] = useState(false);
  // The raw snapshots, not the computed diff: diff rows carry localized field names, so the table
  // is derived below and a locale change re-labels it without re-running the preview.
  const [snapshots, setSnapshots] = useState<{
    currentStrings: StringInfo[];
    targetStrings: StringInfo[];
  } | null>(null);
  // The delta is computed at preview time and is the authoritative payload the
  // Confirm button submits — recomputing at submit time would fetch twice and
  // risk previewing one thing while submitting another.
  const [delta, setDelta] = useState<RollbackDelta | null>(null);
  /**
   * The target version's CX ID membership — the list Confirm submits.
   *
   * A group's CX IDs belong to the version, not to the group like
   * {@link RollbackModalProps.sourceLocale}, so rolling back must restore the
   * target version's list. Submitting the page's current list instead left the
   * CX ID half of every rollback a no-op, and could produce a version that
   * never existed: the target version's strings beside the current CX IDs.
   *
   * Held from the preview for the same reason `delta` is — what was previewed
   * is what Confirm sends.
   */
  const [targetCxIds, setTargetCxIds] = useState<string[]>([]);
  /** The current version's CX IDs, for the side-by-side preview only. */
  const [currentCxIds, setCurrentCxIds] = useState<string[]>([]);
  /**
   * The CX ID change the rollback would make, and the second half of the no-op
   * test: a version whose strings match but whose CX IDs differ is NOT
   * identical, though it previously reported itself as such.
   */
  const [cxIdDiff, setCxIdDiff] = useState<CxIdDiff>({
    added: [],
    removed: [],
  });
  // The target version the current preview outcome (delta/diffResults/noOp)
  // was computed for. Reopening for a different version keeps this modal
  // mounted and only resets state from a post-paint effect, so this is
  // compared against the requested `version` to keep the previous run's
  // preview off-screen until the new one lands (see `previewMatchesRequest`).
  const [resultVersion, setResultVersion] = useState<number | null>(null);
  /**
   * Whether the previewed delta rewrites or deletes strings that are already
   * live — what makes an MCM required inside a window (design §8.3).
   *
   * Computed in the preview effect and held alongside `delta` rather than
   * derived at render time, for the same reason `delta` is: the preview's answer
   * is the one Confirm submits, so the field shown and the payload sent are
   * decided from a single snapshot read. `false` until a preview lands, which is
   * also what keeps the alert and field off-screen while the spinner shows.
   */
  const [editsLiveStrings, setEditsLiveStrings] = useState(false);

  // Recipient for the completion notification. Required on every rollback, the
  // same as a routine submit (SubmitTranslationModal) — a rollback is submitted
  // through the same mutation and the operator should be told when it finishes.
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);

  // Change-control override (MCM), collected only when the window is active AND
  // the delta is not exempt.
  const [mcmId, setMcmId] = useState('');
  const [overrideTouched, setOverrideTouched] = useState(false);

  // Submit phase (runs on Confirm).
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [newVersion, setNewVersion] = useState<number | null>(null);

  const [noOpDetected, setNoOpDetected] = useState(false);
  /**
   * The submit-phase failure, already worded by `submitGroupForTranslation`; the preview-phase
   * one is held as thrown and worded at render.
   */
  const [submitError, setSubmitError] = useState<SubmitFailure | null>(null);
  const [previewFailure, setPreviewFailure] = useState<{
    thrown: unknown;
  } | null>(null);

  // Two separate hooks so the parallel snapshot fetches don't share an
  // ObservableQuery (one hook cannot service two concurrent queries).
  const [fetchCurrentStrings] = useListStringsLazyQuery();
  const [fetchTargetStrings] = useListStringsLazyQuery();
  // CX IDs are version-level group metadata, read via describeStringGroup
  // rather than the ListStrings snapshot (the same split DiffModal uses). Two
  // independent hooks again, so both version reads run in parallel.
  const [fetchCurrentGroup] = useDescribeStringGroupLazyQuery();
  const [fetchTargetGroup] = useDescribeStringGroupLazyQuery();
  const [submitMutation] = useSubmitStringGroupTranslationMutation();

  // Compute the rollback preview when the modal opens. The parent keeps this
  // modal mounted and only toggles `visible`, so every open re-fetches and
  // re-resets — a stale success/error/preview from a previous run must never
  // bleed into a fresh open. The reset here runs from a post-paint effect, so
  // the `previewMatchesRequest` guard below (comparing the loaded version
  // against the requested one) is what actually keeps a previous run's impact
  // counts/diff table off-screen for the frame before this reset lands.
  useEffect(() => {
    if (!visible) {
      return;
    }

    setPreviewLoading(true);
    setSnapshots(null);
    setDelta(null);
    setTargetCxIds([]);
    setCurrentCxIds([]);
    setCxIdDiff({ added: [], removed: [] });
    setSubmitting(false);
    setShowSuccess(false);
    setNewVersion(null);
    setNoOpDetected(false);
    setSubmitError(null);
    setPreviewFailure(null);
    setEditsLiveStrings(false);
    setEmail('');
    setEmailTouched(false);
    setMcmId('');
    setOverrideTouched(false);

    let cancelled = false;

    const run = async () => {
      try {
        // Independent hooks let all four reads load in parallel.
        const [
          currentStrings,
          targetStrings,
          currentVersionCxIds,
          targetVersionCxIds,
        ] = await Promise.all([
          fetchVersionSnapshot(
            fetchCurrentStrings,
            namespaceId,
            stringGroupId,
            currentVersion,
          ),
          fetchVersionSnapshot(
            fetchTargetStrings,
            namespaceId,
            stringGroupId,
            version,
          ),
          fetchVersionCxIds(
            fetchCurrentGroup,
            namespaceId,
            stringGroupId,
            currentVersion,
          ),
          fetchVersionCxIds(
            fetchTargetGroup,
            namespaceId,
            stringGroupId,
            version,
          ),
        ]);

        // computeRollbackDelta is the safety gate: it throws on a corrupt
        // (duplicate-id) snapshot rather than let a destructive rollback be
        // previewed or submitted.
        const computedDelta = computeRollbackDelta(
          currentStrings,
          targetStrings,
          currentVersion,
          version,
        );

        if (cancelled) {
          return;
        }

        // Rolling back restores the target version's CX IDs, so the diff runs
        // current → target.
        const computedCxIdDiff = computeCxIdDiff(
          currentVersionCxIds,
          targetVersionCxIds,
        );
        const cxIdsUnchanged =
          computedCxIdDiff.added.length === 0 &&
          computedCxIdDiff.removed.length === 0;

        // Nothing to roll back only when BOTH halves of a version match. The
        // string delta alone used to decide this, so a version differing only
        // in its CX IDs reported itself as identical to the current one and
        // could not be rolled back at all.
        if (isDeltaEmpty(computedDelta) && cxIdsUnchanged) {
          setNoOpDetected(true);
          setResultVersion(version);
          setPreviewLoading(false);
          return;
        }

        // computeVersionDiff drives the per-string table. It agrees with the
        // delta on which strings changed (same fields, same null/empty
        // normalization), so the preview counts match what Confirm submits.
        setSnapshots({ currentStrings, targetStrings });
        setDelta(computedDelta);
        setCxIdDiff(computedCxIdDiff);
        setCurrentCxIds(currentVersionCxIds);
        setTargetCxIds(targetVersionCxIds);
        // Against the CURRENT snapshot, because that is what is live. Reuses the
        // gate's own per-string comparison via rollbackEditsLiveStrings, so the
        // MCM field appears exactly when the backend would demand one: a delta
        // that only reintroduces missing strings (NewStringsExempted) or only
        // adds CX variants (VariantAdditionsExempted) is exempt and shows
        // neither the alert nor the field.
        setEditsLiveStrings(
          rollbackEditsLiveStrings(computedDelta, currentStrings),
        );
        setResultVersion(version);
        setPreviewLoading(false);
      } catch (err) {
        /* istanbul ignore next -- race-only cleanup: the preview fetch would
           have to reject in the same tick the effect is torn down (visible→false
           or a dep change); not reproducible deterministically in a unit test. */
        if (cancelled) {
          return;
        }
        setResultVersion(version);
        setPreviewLoading(false);
        setPreviewFailure({ thrown: err });
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    visible,
    namespaceId,
    stringGroupId,
    version,
    currentVersion,
    fetchCurrentStrings,
    fetchTargetStrings,
    fetchCurrentGroup,
    fetchTargetGroup,
  ]);

  const diffResults = useMemo<DiffResult[]>(
    () =>
      snapshots
        ? computeVersionDiff(
            t,
            snapshots.currentStrings,
            snapshots.targetStrings,
          )
        : [],
    [t, snapshots],
  );

  const error = useMemo<SubmitFailure | null>(
    () =>
      submitError ??
      (previewFailure
        ? describePreviewFailure(t, locale, previewFailure.thrown)
        : null),
    [t, locale, submitError, previewFailure],
  );

  // The MCM field exists only inside a window AND only when the rollback edits
  // or deletes live strings — the case the freeze is about. An exempt rollback
  // has nothing to authorise, so the field is not rendered rather than offered
  // as optional: a cited-but-unneeded MCM would only be ignored by the gate.
  // When shown, the field is REQUIRED, matching SubmitTranslationModal.
  // One source of truth for "collect an override": the window that applies to
  // this rollback, or null. The alert and the MCM field both key off it, so
  // they cannot show one without the other.
  const activeOverrideWindow =
    changeControlWindow !== null && editsLiveStrings
      ? changeControlWindow
      : null;
  const overrideFieldVisible = activeOverrideWindow !== null;
  const trimmedMcmId = mcmId.trim();
  const overrideErrorKey = mcmOverrideErrorKey(
    overrideFieldVisible,
    trimmedMcmId,
  );
  const overrideErrorText = overrideErrorKey ? t(overrideErrorKey) : undefined;
  const isOverrideValid = overrideErrorKey === undefined;
  const showOverrideError = overrideTouched && !isOverrideValid;

  // Notification email is always collected on a rollback, so validity is a plain
  // function of the trimmed value (unlike the MCM, which is exempt when its field
  // is hidden). Mirrors SubmitTranslationModal.
  const trimmedEmail = email.trim();
  const emailErrorKey = notificationEmailErrorKey(trimmedEmail);
  const emailErrorText = emailErrorKey ? t(emailErrorKey) : undefined;
  const isEmailValid = emailErrorText === undefined;
  const showEmailError = emailTouched && !isEmailValid;

  const handleConfirm = useCallback(async () => {
    // Validated on click rather than by disabling Confirm, like
    // SubmitTranslationModal: the user sees WHY the submission did not go,
    // instead of a dead button.
    setEmailTouched(true);
    setOverrideTouched(true);
    if (!isEmailValid || !isOverrideValid) {
      return;
    }
    await submitGroupForTranslation({
      delta,
      t,
      namespaceId,
      stringGroupId,
      // The TARGET version's list, not the page's current one — see
      // `targetCxIds`. Read from the preview, so this is the membership the
      // operator just confirmed.
      cxIds: targetCxIds,
      sourceLocale,
      cxFamilyId,
      currentVersion,
      version,
      notificationEmail: trimmedEmail,
      // `undefined` when the field is hidden, so an unfrozen or exempt rollback
      // sends byte-identical variables to what it sent before the field existed.
      // When visible, `isOverrideValid` already guarantees a well-formed id.
      mcmId: overrideFieldVisible ? trimmedMcmId : undefined,
      submitMutation,
      setSubmitting,
      setError: setSubmitError,
      setNewVersion,
      setShowSuccess,
    });
  }, [
    t,
    delta,
    namespaceId,
    stringGroupId,
    targetCxIds,
    sourceLocale,
    cxFamilyId,
    currentVersion,
    version,
    submitMutation,
    isEmailValid,
    trimmedEmail,
    isOverrideValid,
    overrideFieldVisible,
    trimmedMcmId,
  ]);

  const handleSuccessDismiss = useCallback(() => {
    setShowSuccess(false);
    setNewVersion(null);
    onSuccess(newVersion ?? version);
  }, [newVersion, version, onSuccess]);

  const handleDismiss = useCallback(() => {
    // The footer Cancel button is disabled while the rollback is in flight, but
    // Cloudscape still dismisses on ESC/backdrop. Ignoring those keeps the
    // modal open until the submit resolves, so a rollback that already
    // succeeded on the backend always reaches onSuccess (and the parent's
    // refetch/flashbar) instead of completing invisibly.
    if (submitting) {
      return;
    }
    onDismiss();
  }, [submitting, onDismiss]);

  if (showSuccess) {
    return (
      <Modal
        visible={visible}
        onDismiss={handleSuccessDismiss}
        header={t('sgd-rollback-success')}
        footer={
          <Box float="right">
            <Button variant="primary" onClick={handleSuccessDismiss}>
              {t('sgd-rollback-success-dismiss')}
            </Button>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Alert type="success">
            {t('sgd-rollback-success-detail', { version, newVersion })}
          </Alert>
          <Box>{t('sgd-rollback-auto-translate')}</Box>
        </SpaceBetween>
      </Modal>
    );
  }

  // Whether the loaded preview outcome belongs to the currently-requested
  // version. On a reopen for a different target version the modal stays
  // mounted and its state is only reset by the post-paint effect above, so
  // this guards the frame in between from showing the previous run's preview.
  const previewMatchesRequest = resultVersion === version;

  // A change-control refusal REPLACES the rollback dialog rather than appearing
  // inside it — the same treatment a refused submit gets on the page. It is the
  // one failure the operator must act on before retrying (cite a different MCM,
  // or wait for the freeze to lift), so it has to be read, not noticed beneath a
  // diff table. Dismissing clears only the refusal and returns to the
  // still-loaded preview, so another MCM can be cited without re-running the
  // snapshot fetch. Guarded on `visible` because ChangeControlBlockedModal keys
  // off the refusal alone and would otherwise outlive a dismissed rollback.
  if (visible && previewMatchesRequest && error?.changeControlBlocked) {
    return (
      <ChangeControlBlockedModal
        refusal={error.changeControlBlocked}
        recovery={t(ROLLBACK_REFUSAL_RECOVERY_KEY)}
        onDismiss={() => setSubmitError(null)}
      />
    );
  }

  // The preview is ready to show once loading finished, no error/no-op tripped,
  // a delta was computed, and that delta belongs to this version.
  const previewReady =
    previewMatchesRequest &&
    !previewLoading &&
    !error &&
    !noOpDetected &&
    !!delta;

  // A rollback can change strings, CX IDs, or both, so each section renders
  // only when it has something to show — a CX-ID-only rollback would otherwise
  // render an empty string table reading "No differences found between
  // versions", contradicting the preview it sits in.
  const hasStringChanges = diffResults.length > 0;
  const hasCxIdChanges =
    cxIdDiff.added.length > 0 || cxIdDiff.removed.length > 0;

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      size="large"
      header={t('sgd-rollback-modal-header', { version })}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={handleDismiss} disabled={submitting}>
              {t('string-cancel')}
            </Button>
            {!noOpDetected && (
              <Button
                variant="primary"
                onClick={handleConfirm}
                loading={previewLoading || submitting}
                disabled={submitting || !previewReady || !email.trim()}
              >
                {t('sgd-confirm')}
              </Button>
            )}
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {previewMatchesRequest && noOpDetected ? (
          <Alert type="info">
            {t('sgd-rollback-no-op', { version, currentVersion })}
          </Alert>
        ) : previewLoading || !previewMatchesRequest ? (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
            <Box variant="p" color="text-body-secondary" margin={{ top: 's' }}>
              {t('sgd-rollback-calculating')}
            </Box>
          </Box>
        ) : previewReady ? (
          <>
            {isCurrentVersionCancelled ? (
              <Alert type="warning">{t('sgd-rollback-source-cancelled')}</Alert>
            ) : (
              <Alert type="warning">
                {t('sgd-rollback-preview-notice', { version, currentVersion })}
              </Alert>
            )}
            {activeOverrideWindow && (
              <Alert
                type={
                  activeOverrideWindow.severity ===
                  ChangeControlSeverity.Blocked
                    ? 'error'
                    : 'warning'
                }
                statusIconAriaLabel={
                  activeOverrideWindow.severity ===
                  ChangeControlSeverity.Blocked
                    ? t('status-icon-error')
                    : t('status-icon-warning')
                }
                header={t(WINDOW_LABEL_KEY[activeOverrideWindow.severity])}
              >
                {/* `reason` is the freeze name from the change-control
                    calendar and is passed through untranslated; the surrounding
                    sentence is a single resource so each locale controls its
                    own word order. */}
                {t('sgd-cc-window-rollback-notice', {
                  reason: activeOverrideWindow.reason,
                  until: formatDayMonthYearTimeUtc(
                    locale,
                    new Date(activeOverrideWindow.windowEnd),
                  ),
                })}
              </Alert>
            )}
            {hasStringChanges && (
              <VersionDiffTable
                results={diffResults}
                previousHeader={t('sgd-diff-column-current', {
                  version: currentVersion,
                })}
                currentHeader={t('sgd-rollback-column-after', { version })}
                badgeLabel={(changeType) => rollbackBadgeLabel(t, changeType)}
                scrollable
              />
            )}
            {hasCxIdChanges && (
              <CxIdDiffTable
                previousCxIds={currentCxIds}
                currentCxIds={targetCxIds}
                previousHeader={t('sgd-diff-column-current', {
                  version: currentVersion,
                })}
                currentHeader={t('sgd-rollback-column-after', { version })}
              />
            )}
            <FormField
              label={t('sgd-notification-email')}
              description={t('sgd-notification-email-help')}
              errorText={showEmailError ? emailErrorText : undefined}
            >
              <Input
                value={email}
                onChange={({ detail }) => setEmail(detail.value)}
                onBlur={() => setEmailTouched(true)}
                placeholder={NOTIFICATION_EMAIL_PLACEHOLDER}
                type="email"
                ariaRequired={true}
              />
            </FormField>
            {overrideFieldVisible && (
              <FormField
                label={t('sgd-mcm-id')}
                description={t(MCM_ID_DESCRIPTION_KEY)}
                errorText={showOverrideError ? overrideErrorText : undefined}
              >
                <Input
                  value={mcmId}
                  onChange={({ detail }) => setMcmId(detail.value)}
                  onBlur={() => setOverrideTouched(true)}
                  placeholder={MCM_ID_PLACEHOLDER}
                  ariaRequired={true}
                />
              </FormField>
            )}
          </>
        ) : null}
        {/*
          MODAL-level, next to the Confirm that produced it, so the previewed
          impact stays on screen and the user can act on the answer. A refusal
          carries the service's wording alone; a fault keeps the generic framing
          it arrived with.
        */}
        {previewMatchesRequest &&
          error &&
          (error.accessDenied ? (
            <AccessDeniedAlert
              message={error.message}
              deniedNamespace={error.deniedNamespace}
              data-testid="rollback-access-denied"
            />
          ) : (
            <Alert type="error">{error.message}</Alert>
          ))}
      </SpaceBetween>
    </Modal>
  );
};

export default RollbackModal;
